import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { FadedPaper } from '../../theme/StyledFields';
import KeyTakeaway from '../team-report/KeyTakeaway';
import VizToggle, { VizType } from '../team-report/VizToggle';
import { SlideDurationRow, ReportSection, CANONICAL_SLIDE_TITLES, formatDuration } from './types';

interface Props {
    data: ReportSection<SlideDurationRow>;
}

function getHeatColor(value: number, min: number, max: number, theme: any): string {
    if (max === min) return theme.palette.primary.light;
    const ratio = (value - min) / (max - min);
    const r = Math.round(255 * ratio);
    const g = Math.round(255 * (1 - ratio * 0.6));
    const b = Math.round(100 * (1 - ratio));
    return `rgb(${r}, ${g}, ${b})`;
}

const SlideDurationSection: React.FC<Props> = ({ data }) => {
    const [viz, setViz] = useState<VizType>('table');
    const theme = useTheme();

    const allAvgs = data.rows.map((r) => r.avg_seconds);
    const globalMin = Math.min(...allAvgs);
    const globalMax = Math.max(...allAvgs);

    const sessionNumbers = useMemo(() => {
        const set = new Set(data.rows.map((r) => r.session_number));
        return Array.from(set).sort((a, b) => a - b);
    }, [data.rows]);

    const slideNumbers = Array.from({ length: 10 }, (_, i) => i + 1);

    const lookup = useMemo(() => {
        const map: Record<string, SlideDurationRow> = {};
        data.rows.forEach((r) => {
            map[`${r.session_number}-${r.slide_number}`] = r;
        });
        return map;
    }, [data.rows]);

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-slide-duration">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">2. Slide Duration Heatmap</Typography>
                <VizToggle value={viz} options={['range', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Average duration per slide position across all athletes. Darker cells = more time spent.
            </Typography>

            {viz === 'range' && (
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Session</TableCell>
                                {slideNumbers.map((sn) => (
                                    <TableCell key={sn} align="center" sx={{ fontSize: 10, minWidth: 70, px: 0.5 }}>
                                        {CANONICAL_SLIDE_TITLES[sn]?.split(' ').slice(0, 2).join(' ') || `S${sn}`}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sessionNumbers.map((sesNum) => (
                                <TableRow key={sesNum}>
                                    <TableCell sx={{ fontWeight: 600 }}>S{sesNum}</TableCell>
                                    {slideNumbers.map((slNum) => {
                                        const row = lookup[`${sesNum}-${slNum}`];
                                        if (!row) {
                                            return <TableCell key={slNum} align="center" sx={{ color: 'text.disabled' }}>--</TableCell>;
                                        }
                                        return (
                                            <TableCell
                                                key={slNum}
                                                align="center"
                                                sx={{
                                                    backgroundColor: getHeatColor(row.avg_seconds, globalMin, globalMax, theme),
                                                    color: row.avg_seconds > (globalMin + globalMax) / 2 ? '#fff' : 'inherit',
                                                    fontWeight: 500,
                                                    fontSize: 11,
                                                }}
                                                title={`${CANONICAL_SLIDE_TITLES[slNum]} — ${formatDuration(row.avg_seconds)} avg (${row.completion_count} completed)`}
                                            >
                                                {formatDuration(row.avg_seconds)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {viz === 'table' && (
                <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Session</TableCell>
                                <TableCell>Slide</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Avg</TableCell>
                                <TableCell align="right">Min</TableCell>
                                <TableCell align="right">Max</TableCell>
                                <TableCell align="right">n</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.rows.map((r) => (
                                <TableRow key={`${r.session_number}-${r.slide_number}`}>
                                    <TableCell>{r.session_number}</TableCell>
                                    <TableCell>{r.slide_number}</TableCell>
                                    <TableCell>{CANONICAL_SLIDE_TITLES[r.slide_number] || `Slide ${r.slide_number}`}</TableCell>
                                    <TableCell>{r.slide_type}</TableCell>
                                    <TableCell align="right">{formatDuration(r.avg_seconds)}</TableCell>
                                    <TableCell align="right">{formatDuration(r.min_seconds)}</TableCell>
                                    <TableCell align="right">{formatDuration(r.max_seconds)}</TableCell>
                                    <TableCell align="right">{r.completion_count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <KeyTakeaway text={data.key_takeaway} />
        </FadedPaper>
    );
};

export default SlideDurationSection;
